import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { getDatabaseStatus } from '../../../api/systemApi';
import { useSettings } from '../../../contexts/SettingsContext';
import ConnectionStatus from './ConnectionStatus';
import AdminActions from './AdminActions';

const SystemHealthTab = () => {
    const { availableClients } = useSettings();
    const [databaseStatus, setDatabaseStatus] = useState(null);
    const [selectedClient, setSelectedClient] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalClient, setModalClient] = useState(null);
    const [filter, setFilter] = useState('all');

    // Fetch database status
    const fetchDatabaseStatus = async (clientId = null) => {
        try {
            setLoading(true);
            setError(null);
            const data = await getDatabaseStatus(clientId);
            setDatabaseStatus(data);
        } catch (err) {
            console.error('Failed to fetch database status:', err);
            setError({
                code: err.response?.status || err.code || 'UNKNOWN',
                title: err.response?.statusText || err.name || 'Error',
                message: err.response?.data?.message || err.message || 'Failed to load database status'
            });
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchDatabaseStatus();
    }, []);

    // Listen for global refresh events
    useEffect(() => {
        const handleGlobalRefresh = () => {
            fetchDatabaseStatus(selectedClient || null);
        };

        window.addEventListener('globalRefresh', handleGlobalRefresh);
        return () => window.removeEventListener('globalRefresh', handleGlobalRefresh);
    }, [selectedClient]);

    const handleClientChange = (clientId) => {
        setSelectedClient(clientId);
        fetchDatabaseStatus(clientId || null);
    };

    const handleClientClick = (client) => {
        setModalClient(client);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setModalClient(null);
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'text-green-500';
            case 'degraded':
                return 'text-yellow-500';
            case 'disconnected':
            case 'error':
                return 'text-red-500';
            default:
                return 'text-gray-500';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'connected':
                return 'mdi:check-circle';
            case 'degraded':
                return 'mdi:alert-circle';
            case 'disconnected':
            case 'error':
                return 'mdi:close-circle';
            default:
                return 'mdi:help-circle';
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col gap-4 w-full">
                <div className="flex flex-row justify-between items-center">
                <h3 className="text-xl flex flex-row gap-2 items-center">
                    <Icon icon='mdi:heart-pulse' className="text-xl text-primary" />
                    System Health
                </h3>
                </div>
                <div className="flex items-center justify-center p-8">
                    <div className="text-font-secondary">Loading system health status...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4 w-full overflow-y-auto scrollable">
            <div className="flex flex-col items-start md:items-center md:flex-row justify-between gap-3">
                <h3 className="subheader-textbox">
                    {/* <Icon icon='mdi:heart-pulse' className="text-xl text-primary" /> */}
                    System Health
                </h3>
                
                {/* Client Filter */}
                <div className="flex items-center gap-2">
                    <label className="text-sm text-font-secondary">Client Filter:</label>
                    <select
                        value={selectedClient}
                        onChange={(e) => handleClientChange(e.target.value)}
                        className="px-3 py-2 button-container rounded text-sm border border-bgAccent focus:border-primary focus:outline-none"
                    >
                        <option value="">All Clients</option>
                        {availableClients.map((client) => (
                            <option key={client.id} value={client.id}>
                                {client.displayName || client.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <div className="p-3 bg-red-900 bg-opacity-20 border border-red-500 rounded-lg">
                    <div className="flex items-center gap-2">
                        <Icon icon="mdi:alert-circle" className="text-red-500" />
                        <span className="text-red-300 text-sm font-medium">
                            {error.code && `[${error.code}]`} {error.title || 'Error'}
                        </span>
                    </div>
                    <p className="text-red-200 text-xs mt-1">{error.message || error}</p>
                </div>
            )}

            <div className="flex flex-col gap-3 pb-6">
                <ConnectionStatus 
                    databaseStatus={databaseStatus}
                    onClientClick={handleClientClick}
                    filter={filter}
                    setFilter={setFilter}
                />
                <AdminActions 
                    onActionSuccess={() => fetchDatabaseStatus(selectedClient || null)}
                />
            </div>

            {/* Client Details Modal */}
            {isModalOpen && modalClient && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="card-container p-6 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-font-primary">
                                Client Details: <span className="font-mono font-bold">{modalClient.clientId}</span>
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-font-secondary hover:text-font-primary transition-colors"
                            >
                                <Icon icon="mdi:close" className="text-xl" />
                            </button>
                        </div>
                        
                        <hr className="border-border" />
                        
                        <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm text-font-secondary">Status</label>
                                    <div className={`flex items-center gap-2 mt-1 ${getStatusColor(modalClient.status)}`}>
                                        <Icon icon={getStatusIcon(modalClient.status)} />
                                        <span className="font-medium">{modalClient.status}</span>
                                    </div>
                                </div>
                                
                                {modalClient.responseTime && (
                                    <div>
                                        <label className="text-sm text-font-secondary">Response Time</label>
                                        <div className="text-font-primary mt-1">{modalClient.responseTime}</div>
                                    </div>
                                )}
                            </div>
                            
                            {modalClient.container && (
                                <div>
                                    <label className="text-sm text-font-secondary">Container</label>
                                    <div className="text-font-primary mt-1 font-mono text-sm bg-bgAccent px-2 py-1 rounded">
                                        {modalClient.container}
                                    </div>
                                </div>
                            )}
                            
                            {modalClient.hasBlobs !== undefined && (
                                <div>
                                    <label className="text-sm text-font-secondary">Has Blobs</label>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Icon 
                                            icon={modalClient.hasBlobs ? "mdi:check-circle" : "mdi:close-circle"} 
                                            className={modalClient.hasBlobs ? "text-green-500" : "text-red-500"} 
                                        />
                                        <span className="text-font-primary">
                                            {modalClient.hasBlobs ? "Yes" : "No"}
                                        </span>
                                    </div>
                                </div>
                            )}
                            
                            {modalClient.error && (
                                <div>
                                    <label className="text-sm text-font-secondary">Error Details</label>
                                    <div className="text-font-primary mt-1 p-3 bg-red-400 bg-opacity-20 rounded text-sm">
                                        {modalClient.error}
                                    </div>
                                </div>
                            )}
                            
                            {modalClient.isKnownIssue && (
                                <div className="flex items-center gap-2 p-3 bg-yellow-400 bg-opacity-20 rounded">
                                    <Icon icon="mdi:information" className="text-yellow-500" />
                                    <span className="text-font-primary text-sm">This is a known issue and may be intentionally restricted</span>
                                </div>
                            )}
                            
                            {modalClient.isNotConfigured && (
                                <div className="flex items-center gap-1 p-3 bg-blue-400 bg-opacity-20 rounded">
                                    <Icon icon="mdi:information" className="text-blue-500" />
                                    <span className="text-font-primary text-sm">This client needs blob storage configuration</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SystemHealthTab;